import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChefHat, Mail, Instagram } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';

/**
 * Shared layout for /privacy and /terms.
 * `docKey` is 'privacy' or 'terms' — picked up from i18n.legal[docKey].
 */
const LegalLayout = ({ docKey }) => {
  const { t, lang, toggleLang } = useLanguage();
  const doc = t.legal[docKey];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [docKey]);

  return (
    <div className="min-h-screen bg-[#FFFDF8] text-stone-900 flex flex-col">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#FFFDF8]/80 border-b border-stone-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid={`legal-back-${docKey}`}
            className="flex items-center gap-2 group"
          >
            <span className="h-9 w-9 rounded-2xl bg-orange-500 flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(249,115,22,0.6)] transition-transform group-hover:rotate-[-6deg]">
              <ChefHat className="h-5 w-5 text-white" strokeWidth={2.4} />
            </span>
            <span className="font-display font-extrabold text-lg tracking-tight">
              MealMatcher
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              data-testid={`legal-lang-toggle-${docKey}`}
              onClick={toggleLang}
              aria-label="Toggle language"
              className="flex items-center gap-0 rounded-full border border-stone-200 bg-white p-1 text-xs font-bold"
            >
              <span
                className={`px-2.5 py-1 rounded-full transition-all ${
                  lang === 'no' ? 'bg-stone-900 text-white' : 'text-stone-500'
                }`}
              >
                NO
              </span>
              <span
                className={`px-2.5 py-1 rounded-full transition-all ${
                  lang === 'en' ? 'bg-stone-900 text-white' : 'text-stone-500'
                }`}
              >
                EN
              </span>
            </button>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-stone-700 hover:bg-stone-100"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t.legal.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs font-bold text-orange-600 mb-6 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t.legal.back}
          </Link>
          <h1
            data-testid={`legal-title-${docKey}`}
            className="font-display font-extrabold text-4xl md:text-5xl tracking-tight mb-3 text-balance"
          >
            {doc.title}
          </h1>
          <p className="text-sm text-stone-500 mb-8">{t.legal.lastUpdated}</p>
          <p className="text-lg text-stone-700 leading-relaxed mb-12">{doc.intro}</p>

          <div className="space-y-10">
            {doc.sections.map((s, i) => (
              <section key={i}>
                <h2 className="font-display font-extrabold text-xl md:text-2xl tracking-tight mb-3">
                  {s.h}
                </h2>
                <div
                  className="text-stone-700 leading-relaxed [&_a]:hover:text-orange-700"
                  dangerouslySetInnerHTML={{ __html: s.p }}
                />
              </section>
            ))}
          </div>

          <div className="mt-16 pt-8 border-t border-stone-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="text-sm text-stone-500">
              MealMatcher · Org.nr. 928 846 288 · Skokkabakken 2 B, 4330 Ålgård
            </div>
            <a
              href="mailto:corporate@mealmatcher.no"
              className="inline-flex items-center gap-2 text-sm font-semibold text-stone-700 hover:text-orange-600"
            >
              <Mail className="h-4 w-4" /> corporate@mealmatcher.no
            </a>
          </div>
        </article>
      </main>

      <footer className="border-t border-stone-200 bg-[#FFFDF8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-stone-500">
          <span>{t.footer.copyright}</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-orange-600">
              {t.footer.links.privacy}
            </Link>
            <Link to="/terms" className="hover:text-orange-600">
              {t.footer.links.terms}
            </Link>
            <a
              href="https://www.instagram.com/mealmatcher.socials/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-orange-600 inline-flex items-center gap-1.5"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const PrivacyPage = () => <LegalLayout docKey="privacy" />;
export const TermsPage = () => <LegalLayout docKey="terms" />;
